/**
 * Drag and drop to change the order of a list (notes, attachments). Each list has its own state, so an item can
 * only be dropped into the list it came from.
 *
 *   const drag = useDragReorder(ids, (id, order) => ...);
 *   <div {...drag.target(id)}>  <span {...drag.handle(id)}>grab here</span> ... </div>
 */
import { useState, type DragEvent } from 'react';

type Pos = 'before' | 'after';

/** `onReorder(id, order)`: `id` was dropped; `order` is the new order of all ids, as displayed. */
export function useDragReorder(ids: number[], onReorder: (id: number, order: number[]) => void, enabled = true) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<{ id: number; pos: Pos } | null>(null);

  const reset = () => {
    setDragging(null);
    setOver(null);
  };
  const position = (e: DragEvent<HTMLElement>): Pos => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY < r.top + r.height / 2 ? 'before' : 'after';
  };

  return {
    /** the element to grab */
    handle: (id: number) =>
      enabled && ids.length > 1
        ? {
            draggable: true,
            className: 'drag-handle',
            onDragStart: (e: DragEvent<HTMLElement>) => {
              e.stopPropagation();
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', ''); // Firefox starts a drag only with data
              setDragging(id);
            },
            onDragEnd: reset,
          }
        : {},
    /** the item; shows where the dragged one would land */
    target: (id: number) => ({
      className: dragging === id ? 'drag-source' : over?.id === id ? `drop-${over.pos}` : '',
      onDragOver: (e: DragEvent<HTMLElement>) => {
        if (dragging === null) return; // not from this list
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const pos = position(e);
        if (over?.id !== id || over.pos !== pos) setOver({ id, pos });
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        if (dragging === null) return;
        e.preventDefault();
        e.stopPropagation();
        const moved = dragging;
        const pos = position(e);
        reset();
        if (moved === id) return;
        const order = ids.filter((x) => x !== moved);
        order.splice(order.indexOf(id) + (pos === 'after' ? 1 : 0), 0, moved);
        if (order.some((x, i) => x !== ids[i])) onReorder(moved, order);
      },
    }),
  };
}

/** The id placed directly before `id` in `order` (null: first): what the server's move endpoints expect. */
export const afterId = (order: number[], id: number): number | null => {
  const i = order.indexOf(id);
  return i > 0 ? order[i - 1] : null;
};
